import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/Schedule.css';

function Schedule() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [uploadedAt, setUploadedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('future');
  const fileRef = useRef(null);

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/ppr/schedule');
      setEntries(res.data.data || []);
      setUploadedAt(res.data.uploadedAt || null);
    } catch {
      setError('Ошибка загрузки графика');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post('/api/ppr/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchSchedule();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка загрузки файла');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const todayISO = new Date().toISOString().slice(0, 10);
  const currentMonthISO = todayISO.slice(0, 7);

  const filtered = entries
    .filter(e => {
      if (dateFilter === 'future') return e.date >= todayISO;
      if (dateFilter === 'current') return e.date.startsWith(currentMonthISO);
      return true;
    })
    .filter(e => !search || e.address.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.date !== b.date ? a.date.localeCompare(b.date) : a.address.localeCompare(b.address));

  return (
    <div className="container mt-5 mb-5">
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <h1 className="mb-0">График ППР / ТО</h1>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {uploadedAt && (
            <small className="text-muted">
              Обновлено: {new Date(uploadedAt).toLocaleString('ru-RU')}
            </small>
          )}
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <><span className="spinner-border spinner-border-sm me-1" />Загрузка...</>
            ) : (
              uploadedAt ? 'Обновить таблицу' : 'Загрузить таблицу'
            )}
          </button>
          <input
            type="file"
            ref={fileRef}
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : !uploadedAt ? (
        <div className="text-center py-5 text-muted">
          <p className="mb-2">График не загружен.</p>
          <p className="mb-3">Загрузите файл «График ППР и ТО .xlsx» для начала работы.</p>
          <button className="btn btn-primary" onClick={() => fileRef.current?.click()}>
            Загрузить таблицу
          </button>
        </div>
      ) : (
        <>
          <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: 320 }}
              placeholder="Поиск по объекту..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="btn-group btn-group-sm">
              {[
                { key: 'future', label: 'Предстоящие' },
                { key: 'current', label: 'Текущий месяц' },
                { key: 'all', label: 'Все' },
              ].map(f => (
                <button
                  key={f.key}
                  type="button"
                  className={`btn ${dateFilter === f.key ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setDateFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="schedule-table-wrapper">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 50 }}>№</th>
                  <th style={{ width: 140 }}>Дата</th>
                  <th>Объект</th>
                  <th style={{ width: 160 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      Нет записей по выбранному фильтру
                    </td>
                  </tr>
                ) : (
                  filtered.map((entry, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{entry.date.split('-').reverse().join('.')}</td>
                      <td>{entry.address}</td>
                      <td>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => navigate(`/new-report?${new URLSearchParams({ date: entry.date, address: entry.address }).toString()}`)}
                        >
                          Создать отчёт
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-2">
            <small className="text-muted">
              Показано {filtered.length} из {entries.length} записей
            </small>
          </div>
        </>
      )}
    </div>
  );
}

export default Schedule;
